import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { ApplicationInsightsService } from '@services/application-insights.service';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AuthService } from '@services/auth.service';
import { environment } from '@environments/environment';
import { MOCK_USERS, MockUser } from '@environments/mock-users';

/**
 * Combines isauthenticated guard and hasfeature guard.
 * Since this logic is asynchronous both could be run, when in fact
 * we don't want hasfeature to run if isauthenticated fails.
 * @see https://stackoverflow.com/a/40590812/1180671
 */
@Injectable({
  providedIn: 'root',
})
export class IsLoggedInGuard  {
  constructor(private readonly authService: AuthService, private readonly loggingService: ApplicationInsightsService) {}

  canActivate(_route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    if (this.shouldSkipAuth()) {
      return this.handleMockAuthentication();
    }

    return this.handleRealAuthentication(state);
  }

  private shouldSkipAuth(): boolean {
    return environment.skipAuth === true && environment.mockUser !== undefined;
  }

  private handleMockAuthentication(): Observable<boolean> {
    const mockUser = environment.useMSW ? this.getMockUser() : environment.mockUser!;
    this.authService.setMockUser(mockUser);
    return of(true);
  }

  private getMockUser(): MockUser {
    const params = new URLSearchParams(window.location.search);
    const scenario = params.get('mockUser');
    if (scenario && MOCK_USERS[scenario]) {
      return MOCK_USERS[scenario];
    }
    return environment.mockUser!;
  }

  private handleRealAuthentication(state: RouterStateSnapshot): Observable<boolean> {
    return this.authService.checkAuth().pipe(
      catchError((e) => this.handleAuthError(e)),
      map((authResult) => this.processAuthResult(authResult, state)),
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private handleAuthError(error: any): Observable<{ isAuthenticated: boolean }> {
    this.loggingService.trackException(error);
    return of({ isAuthenticated: false });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private processAuthResult(authResult: any, state: RouterStateSnapshot): boolean {
    if (!authResult.isAuthenticated) {
      this.authService.authorize(state.url);
      return false;
    }
    return true;
  }
}
